import { motion } from "framer-motion";
import { Factory, Boxes, Wrench, ClipboardCheck, PenTool, Droplets } from "lucide-react";

const IMG_MANUFACTURING =
  "https://images.unsplash.com/photo-1713593930871-e21d7f9ef4a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxzY2FmZm9sZGluZyUyMGNvbnN0cnVjdGlvbiUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzg0MzI3MjkzfDA&ixlib=rb-4.1.0&q=85";
const IMG_INSPECTION =
  "https://images.unsplash.com/photo-1603239564387-c5b5ea6f635e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxzY2FmZm9sZGluZyUyMGNvbnN0cnVjdGlvbiUyMGluZHVzdHJpYWx8ZW58MHx8fHwxNzg0MzI3MjkzfDA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    no: "01",
    title: "Manufacturing",
    desc: "Precision fabrication of cuplock scaffolding equipment built to withstand the harshest industrial environments.",
    icon: Factory,
    span: "md:col-span-2 md:row-span-2",
    image: IMG_MANUFACTURING,
  },
  {
    no: "02",
    title: "Rental",
    desc: "Flexible cuplock scaffolding rental for projects of any scale, anywhere in India.",
    icon: Boxes,
    span: "md:col-span-2",
  },
  {
    no: "03",
    title: "Erection & Dismantling",
    desc: "Certified crews for rapid, safe assembly and teardown on live industrial sites.",
    icon: Wrench,
    span: "md:col-span-2",
  },
  {
    no: "04",
    title: "Inspection",
    desc: "Rigorous scaffolding inspection & compliance audits.",
    icon: ClipboardCheck,
    span: "md:col-span-1",
    image: IMG_INSPECTION,
  },
  {
    no: "05",
    title: "GA Drawing",
    desc: "Detailed general arrangement drawings & engineering.",
    icon: PenTool,
    span: "md:col-span-1",
  },
  {
    no: "06",
    title: "Hydrojetting",
    desc: "High-pressure hydrojetting services for industrial cleaning and surface preparation.",
    icon: Droplets,
    span: "md:col-span-2",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-background px-6 md:px-12 lg:px-16 py-24 md:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            [ 02 — Capabilities ]
          </span>
          <h2 className="font-bebas uppercase leading-[0.85] text-foreground text-6xl md:text-8xl mt-4">
            What We Build
          </h2>
        </div>
        <p className="font-plex text-muted-foreground max-w-sm">
          Six integrated services delivering end-to-end scaffolding solutions for
          India&apos;s heaviest industries.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[220px] border-t border-l border-border">
        {services.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.no}
              data-testid={`service-card-${s.no}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`group relative overflow-hidden border-b border-r border-border p-7 flex flex-col justify-between ${s.span}`}
            >
              {s.image && (
                <div className="absolute inset-0">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="h-full w-full object-cover grayscale opacity-25 group-hover:opacity-40 group-hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
                </div>
              )}
              <div className="relative flex items-center justify-between">
                <Icon className="text-primary" size={28} strokeWidth={1.5} />
                <span className="font-mono text-xs text-muted-foreground">{s.no}</span>
              </div>
              <div className="relative">
                <h3 className="font-bebas uppercase text-3xl md:text-4xl text-foreground">
                  {s.title}
                </h3>
                <p className="font-plex text-sm text-muted-foreground mt-2 max-w-md">
                  {s.desc}
                </p>
              </div>
              <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-primary group-hover:w-full transition-all duration-500" />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
